import http from '../http'

export function getSettlementSalesSummaryFilterOptions() {
  return http.get('/settlement-sales-summary/filter-options')
}

export function getSettlementPurchaseSummaryFilterOptions() {
  return http.get('/settlement-purchase-summary/filter-options')
}

export function getAccountsPayableSummaryFilterOptions() {
  return http.get('/accounts-payable-summary/filter-options')
}

/**
 * 应收账款汇总表：合同/需求方等筛选项 — 仅「销售合同」（后端：当前单位为供方）
 * 对应 GET /contracts/sales/search ，勿与采购接口混用。
 */
export function fetchSalesContractsFilterPage(params = {}) {
  const queryParams = {
    page: params.page !== undefined ? (params.page - 1) : 0,
    size: params.size || 200,
    sortBy: params.sortBy || 'createTime',
    sortDir: params.sortDir || 'desc'
  }
  if (params.contractName) queryParams.contractName = params.contractName
  if (params.contractNumber) queryParams.contractNumber = params.contractNumber
  if (params.creatorId != null) queryParams.creatorId = params.creatorId
  if (params.status) queryParams.status = params.status
  return http.get('/contracts/sales/search', { params: queryParams })
}

/**
 * 应付账款汇总表：合同/供应商等筛选项 — 仅「采购合同」（后端：当前单位为需方且排除同时为供方）
 * 对应 GET /contracts/purchase/search ，勿与销售接口混用。
 */
export function fetchPurchaseContractsFilterPage(params = {}) {
  const queryParams = {
    page: params.page !== undefined ? (params.page - 1) : 0,
    size: params.size || 200,
    sortBy: params.sortBy || 'createTime',
    sortDir: params.sortDir || 'desc'
  }
  if (params.contractName) queryParams.contractName = params.contractName
  if (params.contractNumber) queryParams.contractNumber = params.contractNumber
  if (params.creatorId != null) queryParams.creatorId = params.creatorId
  if (params.status) queryParams.status = params.status
  return http.get('/contracts/purchase/search', { params: queryParams })
}

/**
 * 从 ApiResponse 分页 data 中取出合同列表与 total（兼容 content / records / list）
 * @param {Object} apiBody - axios 的 response.data（含 success、data 为分页对象）
 */
export function pickContractFilterPage(apiBody) {
  const page = apiBody && apiBody.data
  if (!page || typeof page !== 'object') {
    return { list: [], total: null }
  }
  let list = []
  if (Array.isArray(page.content)) list = page.content
  else if (Array.isArray(page.records)) list = page.records
  else if (Array.isArray(page.list)) list = page.list
  else if (Array.isArray(page.items)) list = page.items
  const total = page.totalElements != null ? page.totalElements : (page.total != null ? page.total : null)
  return { list, total }
}

/**
 * 获取单位统计数据
 * @param {Number} unitId - 单位ID
 * @returns {Promise} 返回统计数据
 */
export function getUnitStatistics(unitId) {
  return http.get(`/units/${unitId}/statistics`)
}

/**
 * 分页查询销售汇总表（按需求方汇总）
 */
export function getSettlementSalesSummaryAggregated(params = {}) {
  const hasArrayParams = (params.demandUnitNames && params.demandUnitNames.length > 0) ||
    (params.projectNames && params.projectNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/settlement-sales-summary/aggregated', { params, ...config })
}

/**
 * 分页查询销售汇总表明细（按结算单）
 * @param {String} params.scopeDemandUnitName - 汇总行需求方（必填）
 * @param {String} [params.scopedProjectNames] - 汇总行项目名称，逗号分隔
 */
export function getSettlementSalesSummaryDetail(params = {}) {
  const hasArrayParams = (params.demandUnitNames && params.demandUnitNames.length > 0) ||
    (params.projectNames && params.projectNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/settlement-sales-summary/detail', { params, ...config })
}

/**
 * 导出销售汇总表（按需求方）Excel
 */
export function exportSettlementSalesSummaryAggregated(params = {}) {
  const hasArrayParams = (params.demandUnitNames && params.demandUnitNames.length > 0) ||
    (params.projectNames && params.projectNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/settlement-sales-summary/aggregated/export', { params, ...config })
}

/**
 * 导出销售汇总表明细 Excel
 */
export function exportSettlementSalesSummaryDetail(params = {}) {
  const hasArrayParams = (params.demandUnitNames && params.demandUnitNames.length > 0) ||
    (params.projectNames && params.projectNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/settlement-sales-summary/detail/export', { params, ...config })
}

/**
 * 分页查询采购汇总表（按供应商汇总）
 */
export function getSettlementPurchaseSummaryAggregated(params = {}) {
  const hasArrayParams = (params.supplierUnitNames && params.supplierUnitNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/settlement-purchase-summary/aggregated', { params, ...config })
}

/**
 * 分页查询采购汇总表明细（按结算单）
 * @param {String} params.scopeSupplierName - 汇总行供应商（必填）
 */
export function getSettlementPurchaseSummaryDetail(params = {}) {
  const hasArrayParams = (params.supplierUnitNames && params.supplierUnitNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/settlement-purchase-summary/detail', { params, ...config })
}

/**
 * 导出采购汇总表（按供应商）Excel
 */
export function exportSettlementPurchaseSummaryAggregated(params = {}) {
  const hasArrayParams = (params.supplierUnitNames && params.supplierUnitNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/settlement-purchase-summary/aggregated/export', { params, ...config })
}

/**
 * 导出采购汇总表明细 Excel
 */
export function exportSettlementPurchaseSummaryDetail(params = {}) {
  const hasArrayParams = (params.supplierUnitNames && params.supplierUnitNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/settlement-purchase-summary/detail/export', { params, ...config })
}

/**
 * 分页查询应收账款汇总表（按合同）
 */
export function getAccountsReceivableSummary(params = {}) {
  const hasArrayParams = (params.demandUnitNames && params.demandUnitNames.length > 0) ||
    (params.projectNames && params.projectNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/accounts-receivable-summary', { params, ...config })
}

/**
 * 导出应收账款汇总表 Excel
 */
export function exportAccountsReceivableSummary(params = {}) {
  const hasArrayParams = (params.demandUnitNames && params.demandUnitNames.length > 0) ||
    (params.projectNames && params.projectNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/accounts-receivable-summary/export', { params, ...config })
}

/**
 * 分页查询应付账款汇总表（按采购合同）
 */
export function getAccountsPayableSummary(params = {}) {
  const hasArrayParams = (params.supplierUnitNames && params.supplierUnitNames.length > 0) ||
    (params.projectNames && params.projectNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/accounts-payable-summary', { params, ...config })
}

/**
 * 导出应付账款汇总表 Excel
 */
export function exportAccountsPayableSummary(params = {}) {
  const hasArrayParams = (params.supplierUnitNames && params.supplierUnitNames.length > 0) ||
    (params.projectNames && params.projectNames.length > 0) ||
    (params.contractNames && params.contractNames.length > 0)
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/accounts-payable-summary/export', { params, ...config })
}

/**
 * 应付账款明细表（按结算单，需 contractId；当前单位为采购需方）
 */
export function getAccountsPayableSummaryDetail(params = {}) {
  return http.get('/accounts-payable-summary/detail', { params })
}

/**
 * 导出应付账款明细表 Excel（需 contractId，筛选与明细查询一致）
 */
export function exportAccountsPayableSummaryDetail(params = {}) {
  return http.get('/accounts-payable-summary/detail/export', {
    params,
    responseType: 'blob'
  })
}

/**
 * 应收账款明细表（按结算单，需 contractId）
 */
export function getAccountsReceivableSummaryDetail(params = {}) {
  return http.get('/accounts-receivable-summary/detail', { params })
}

/**
 * 导出应收账款明细表 Excel（需 contractId，筛选与明细查询一致）
 */
export function exportAccountsReceivableSummaryDetail(params = {}) {
  return http.get('/accounts-receivable-summary/detail/export', {
    params,
    responseType: 'blob'
  })
}

/**
 * 序列化请求参数，支持数组格式 contractNames=a&contractNames=b
 */
function serializeParams(params) {
  const parts = []
  Object.keys(params).forEach(key => {
    const val = params[key]
    if (Array.isArray(val) && val.length > 0) {
      val.forEach(v => {
        if (v !== undefined && v !== null && v !== '') {
          parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(v))
        }
      })
    } else if (val !== undefined && val !== null && val !== '') {
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(val))
    }
  })
  return parts.join('&')
}

/**
 * 分页查询销售成本统计（按合同）
 * @param {Object} params 查询参数
 * @param {String} [params.contractStartDate] - 合同开始日期，格式：yyyy-MM-dd
 * @param {String} [params.contractEndDate] - 合同结束日期，格式：yyyy-MM-dd
 * @param {String[]} [params.contractNames] - 合同名称（多选）
 * @param {String[]} [params.customerNames] - 客户名称（多选）
 * @param {Number} [params.page] - 页码，默认 0
 * @param {Number} [params.size] - 每页条数，默认 10
 * @param {String} [params.sortBy] - 排序字段，默认 contractDate
 * @param {String} [params.sortDir] - 排序方向 asc/desc，默认 desc
 * @returns {Promise} 返回销售成本统计列表和分页信息
 */
export function getSalesCostStatisticsByContract(params = {}) {
  const hasArrayParams = (params.contractNames && params.contractNames.length > 0) ||
    (params.customerNames && params.customerNames.length > 0)
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/sales-cost-statistics/contracts', { params, ...config })
}

/**
 * 分页查询销售产品明细统计
 * @param {Object} params 查询参数
 * @param {String} [params.productName] - 产品名称（模糊搜索）
 * @param {String} [params.contractStartDate] - 合同开始日期，格式：yyyy-MM-dd
 * @param {String} [params.contractEndDate] - 合同结束日期，格式：yyyy-MM-dd
 * @param {Number} [params.page] - 页码，默认 0
 * @param {Number} [params.size] - 每页条数，默认 10
 * @returns {Promise} 返回销售产品明细列表和分页信息
 */
export function getSalesProductDetails(params = {}) {
  return http.get('/sales-cost-statistics/product-details', { params })
}

/**
 * 导出销售成本统计 Excel
 * @param {Object} params 查询参数（与列表接口相同，不含分页）
 * @param {String} [params.contractStartDate] - 合同开始日期，格式：yyyy-MM-dd
 * @param {String} [params.contractEndDate] - 合同结束日期，格式：yyyy-MM-dd
 * @param {String[]} [params.contractNames] - 合同名称（多选）
 * @param {String[]} [params.customerNames] - 客户名称（多选）
 * @returns {Promise} 返回 Excel 文件流（blob）
 */
export function exportSalesCostStatistics(params = {}) {
  const hasArrayParams = (params.contractNames && params.contractNames.length > 0) ||
    (params.customerNames && params.customerNames.length > 0)
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/sales-cost-statistics/export', { params, ...config })
}

/**
 * 产品明细销售统计（结算日期 + 销售合同名称多选 + 产品名称模糊）
 * 列表行为合同内同产品名称合并后的汇总行。
 */
export function getProductSettlementSalesStatistics(params = {}) {
  const hasArrayParams = params.contractNames && params.contractNames.length > 0
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/product-settlement-sales-statistics', { params, ...config })
}

/**
 * 产品明细销售统计 — 某合同某产品名称下的规格明细行（与列表筛选条件一致）
 */
export function getProductSettlementSalesStatisticsDetailLines(params = {}) {
  const hasArrayParams = params.contractNames && params.contractNames.length > 0
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/product-settlement-sales-statistics/detail-lines', { params, ...config })
}

/**
 * 导出产品明细销售统计 Excel（仍为按规格展开的明细行）
 */
export function exportProductSettlementSalesStatistics(params = {}) {
  const hasArrayParams = params.contractNames && params.contractNames.length > 0
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/product-settlement-sales-statistics/export', { params, ...config })
}

/**
 * 商品入库明细表（采购结算；结算日期 + 供应商多选）
 */
export function getProductPurchaseInboundDetail(params = {}) {
  const hasArrayParams = params.supplierUnitNames && params.supplierUnitNames.length > 0
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/product-purchase-inbound-detail', { params, ...config })
}

/**
 * 导出商品入库明细表 Excel（与表格列一致）
 */
export function exportProductPurchaseInboundDetail(params = {}) {
  const hasArrayParams = params.supplierUnitNames && params.supplierUnitNames.length > 0
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/product-purchase-inbound-detail/export', { params, ...config })
}

/**
 * 商品出库明细表（销售结算；结算日期 + 需求方多选）
 */
export function getProductSalesOutboundDetail(params = {}) {
  const hasArrayParams = params.demandUnitNames && params.demandUnitNames.length > 0
  const config = hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {}
  return http.get('/product-sales-outbound-detail', { params, ...config })
}

/**
 * 导出商品出库明细表 Excel（与表格列一致）
 */
export function exportProductSalesOutboundDetail(params = {}) {
  const hasArrayParams = params.demandUnitNames && params.demandUnitNames.length > 0
  const config = {
    responseType: 'blob',
    ...(hasArrayParams ? { paramsSerializer: p => serializeParams(p) } : {})
  }
  return http.get('/product-sales-outbound-detail/export', { params, ...config })
}

export default {
  fetchSalesContractsFilterPage,
  fetchPurchaseContractsFilterPage,
  pickContractFilterPage,
  getUnitStatistics,
  getSettlementSalesSummaryFilterOptions,
  getSettlementPurchaseSummaryFilterOptions,
  getAccountsPayableSummaryFilterOptions,
  getSalesCostStatisticsByContract,
  getSalesProductDetails,
  exportSalesCostStatistics,
  getSettlementSalesSummaryAggregated,
  getSettlementSalesSummaryDetail,
  exportSettlementSalesSummaryAggregated,
  exportSettlementSalesSummaryDetail,
  getSettlementPurchaseSummaryAggregated,
  getSettlementPurchaseSummaryDetail,
  exportSettlementPurchaseSummaryAggregated,
  exportSettlementPurchaseSummaryDetail,
  getAccountsReceivableSummary,
  getAccountsReceivableSummaryDetail,
  exportAccountsReceivableSummaryDetail,
  exportAccountsReceivableSummary,
  getAccountsPayableSummary,
  getAccountsPayableSummaryDetail,
  exportAccountsPayableSummaryDetail,
  exportAccountsPayableSummary,
  getProductSettlementSalesStatistics,
  getProductSettlementSalesStatisticsDetailLines,
  exportProductSettlementSalesStatistics,
  getProductPurchaseInboundDetail,
  exportProductPurchaseInboundDetail,
  getProductSalesOutboundDetail,
  exportProductSalesOutboundDetail
}
